import { useCallback, useRef, useState } from "react";
import { Loader2, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DocumentPicker, type PickerDocument } from "@/components/documents/DocumentPicker";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import { useOpenLinkedDoc } from "@/domains/pm/hooks/useOpenLinkedDoc";
import type { LinkedDocument } from "@/domains/pm/types";
import type { ProtocolAttachment } from "@/domains/pm/utils/protocolAttachments";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Translations } from "@/lib/i18n/types";
import { AttachMenuItems } from "../AttachMenuItems";
import { LinkedDocumentRow } from "../LinkedDocumentRow";
import { AttachmentGallery, type GalleryAttachment } from "../attachments/AttachmentGallery";
import {
  attachmentPreviewKind,
  storedGalleryPicture,
  type AttachmentPreviewKind,
  type GalleryPicture,
} from "../attachments/attachmentPreview";
import { SectionHeading } from "./SectionHeading";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];

/** One file as this step lists it, whichever list it is in. */
interface ListedAttachment {
  picture: GalleryPicture;
  kind: AttachmentPreviewKind | null;
  onOpen?: () => void;
  onRemove: () => void;
}

/**
 * Files as the issue panel shows them — images and PDFs as a gallery of the
 * pictures themselves, anything else (or whose picture could not be drawn) as
 * a row beneath — each with a way to take it back out of the protocol.
 */
function AttachmentList({
  items,
  buildingId,
  removeLabel,
}: {
  items: ListedAttachment[];
  buildingId: number;
  removeLabel: string;
}) {
  const { t } = useLanguage();
  const [failedKeys, setFailedKeys] = useState<ReadonlySet<string>>(new Set());
  const handlePreviewFailed = useCallback((key: string) => {
    setFailedKeys((current) => (current.has(key) ? current : new Set(current).add(key)));
  }, []);

  const removeButton = (item: ListedAttachment, className: string) => (
    <button
      type="button"
      onClick={item.onRemove}
      aria-label={removeLabel}
      title={removeLabel}
      className={className}
    >
      <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
    </button>
  );

  const gallery: GalleryAttachment[] = [];
  const rows: ListedAttachment[] = [];
  for (const item of items) {
    if (item.kind && !failedKeys.has(item.picture.key)) {
      gallery.push({
        ...item.picture,
        kind: item.kind,
        onOpen: item.onOpen,
        action: removeButton(
          item,
          "flex h-5 w-5 items-center justify-center rounded bg-background/90 shadow-sm"
        ),
      });
    } else {
      rows.push(item);
    }
  }

  return (
    <div>
      {gallery.length > 0 && (
        <AttachmentGallery
          attachments={gallery}
          buildingId={buildingId}
          onPreviewFailed={handlePreviewFailed}
          showAllLabel={t.pm.issues.detail.showAllAttachments}
          showFewerLabel={t.pm.issues.detail.showFewerAttachments}
        />
      )}
      {rows.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {rows.map((item) => (
            <LinkedDocumentRow
              key={item.picture.key}
              filename={item.picture.filename}
              buildingId={buildingId}
              breadcrumbSegments={item.picture.breadcrumbSegments}
              onOpen={item.onOpen}
              unavailableLabel={t.pm.issues.detail.previewUnavailable}
              action={removeButton(
                item,
                "opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
              )}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface ProtocolAttachmentsSectionProps {
  buildingId: number;
  buildingProjectId?: number;
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  copy: ProtocolCopy;
  added: ProtocolAttachment[];
  resolving: boolean;
  onAddFiles: (files: File[]) => void;
  onAddDocuments: (docs: PickerDocument[]) => void;
  onRemove: (key: string) => void;
  /** Whether each file rides inside the PDF; null when there are none. */
  embedOriginals: { checked: boolean; onChange: (checked: boolean) => void } | null;
}

/**
 * Step 3's attachments: files the author adds — from their computer or from
 * Franklin, through the same menu an issue uses. The issues' own files are a
 * sub-option of the issue table, not listed here.
 */
export function ProtocolAttachmentsSection(props: ProtocolAttachmentsSectionProps) {
  const { copy, buildingId } = props;
  const labels = copy.attachments;
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { canOpen, open } = useOpenLinkedDoc(buildingId, props.openViewer);

  const storedItem = (
    doc: LinkedDocument,
    key: string,
    onRemove: () => void
  ): ListedAttachment => ({
    picture: storedGalleryPicture(doc, key),
    kind: attachmentPreviewKind(doc),
    onOpen: canOpen(doc) ? () => open(doc) : undefined,
    onRemove,
  });

  const addedItems = props.added.map((attachment): ListedAttachment => {
    const onRemove = () => props.onRemove(attachment.key);
    if (attachment.origin === "stored") return storedItem(attachment.doc, attachment.key, onRemove);
    const { file, url } = attachment;
    const kind = attachmentPreviewKind({
      filename: file.name,
      content_type: file.type,
      size: file.size,
    });
    return {
      picture: {
        key: attachment.key,
        filename: file.name,
        size: file.size,
        source: { type: "local", url },
      },
      kind,
      // Not in Franklin, so not for the document viewer: the browser opens
      // its own copy in a tab.
      onOpen: kind ? () => void window.open(url, "_blank") : undefined,
      onRemove,
    };
  });

  const alreadyAttachedIds = props.added.flatMap((attachment) =>
    attachment.origin === "stored" ? [String(attachment.doc.id)] : []
  );

  return (
    <section className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <SectionHeading>{labels.heading}</SectionHeading>
          <p className="mt-1 text-xs text-muted-foreground">{labels.hint}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 shrink-0 gap-1.5 text-xs"
              disabled={props.resolving}
            >
              {props.resolving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Paperclip className="h-3.5 w-3.5" />
              )}
              {labels.attach}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <AttachMenuItems
              onAddFromFranklin={() => setPickerOpen(true)}
              onUpload={() => inputRef.current?.click()}
            />
          </DropdownMenuContent>
        </DropdownMenu>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(event) => {
            props.onAddFiles(Array.from(event.target.files ?? []));
            // Cleared so picking the same file again still fires a change.
            event.target.value = "";
          }}
        />
      </div>

      {addedItems.length > 0 && (
        <AttachmentList items={addedItems} buildingId={buildingId} removeLabel={labels.remove} />
      )}

      {props.embedOriginals && (
        <div className="flex items-start gap-3">
          <Checkbox
            id="protocol-embed-originals"
            checked={props.embedOriginals.checked}
            className="mt-0.5"
            onCheckedChange={(value) => props.embedOriginals?.onChange(value === true)}
          />
          <div className="min-w-0 flex-1">
            <label htmlFor="protocol-embed-originals" className="text-sm font-medium">
              {labels.embedOriginals}
            </label>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
              {labels.embedOriginalsHelp}
            </p>
          </div>
        </div>
      )}

      {/* Mounted only while open: closed, it would still run its document
          queries on every render of this step. */}
      {pickerOpen && (
        <DocumentPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          buildingId={String(buildingId)}
          projectId={props.buildingProjectId != null ? String(props.buildingProjectId) : undefined}
          alreadyAttachedIds={alreadyAttachedIds}
          mode="multi"
          onSelect={(docs) => {
            setPickerOpen(false);
            props.onAddDocuments(docs);
          }}
          title={labels.heading}
          submitLabel={labels.attach}
        />
      )}
    </section>
  );
}
